/**
 * Typed client for the Super Admin communications APIs (apps/api/src/routes/platform-email.ts,
 * platform-comms.ts, announcements.ts). Shapes mirror the server responses.
 */
import { api, buildQuery, API_BASE, type Page, type Query } from '@/lib/api';

/* ---------------------------------------------------------------- shared */

export const EMAIL_CATEGORIES = ['invitation', 'password_reset', 'reminder', 'login_alert', 'usage_report', 'inactivity', 'announcement', 'security', 'test', 'other'] as const;
export type EmailCategory = (typeof EMAIL_CATEGORIES)[number];
export const EMAIL_STATUSES = ['queued', 'sent', 'failed', 'delivered', 'bounced', 'suppressed'] as const;
export type EmailStatus = (typeof EMAIL_STATUSES)[number];

export function apiDelete<T>(path: string): Promise<T> {
  return api.raw<{ data: T }>(path, { method: 'DELETE' }).then((b) => b?.data as T);
}

export const commsKeys = {
  emailConfig: ['platform', 'email', 'config'] as const,
  emailStatus: ['platform', 'email', 'status'] as const,
  emailProviders: ['platform', 'email', 'providers'] as const,
  emailTemplates: ['platform', 'email', 'templates'] as const,
  emailLogs: ['platform', 'email-logs'] as const,
  emailSummary: ['platform', 'email-logs', 'summary'] as const,
  inactivity: ['platform', 'inactivity'] as const,
  usageReports: ['platform', 'usage-reports'] as const,
  announcements: ['platform', 'announcements'] as const,
  releaseNotes: ['platform', 'release-notes'] as const,
  activeAnnouncements: ['announcements', 'active'] as const,
  updates: ['updates'] as const,
  updatesUnread: ['updates', 'unread-count'] as const,
};

/* ---------------------------------------------------------------- email config */

export type EmailProviderKind = 'env' | 'dev' | 'log' | 'smtp';

export interface EmailProviderSettings {
  provider: EmailProviderKind;
  fromName: string;
  fromEmail: string;
  replyTo: string;
  smtp: { host: string; port: number; secure: boolean; requireTLS: boolean; username: string };
  rateLimitPerMinute: number;
}

export interface EmailReadiness {
  provider: EmailProviderKind;
  effectiveProvider: string;
  ready: boolean;
  delivers: boolean;
  issues: string[];
  /** Set when a tested, active default provider from the Providers tab handles platform email. */
  managedProvider?: { id: string; name: string; type: string };
}

export type SystemTemplateKey = 'password_reset' | 'workspace_welcome' | 'staff_account_created' | 'client_account_created';
export interface SystemTemplate {
  subject: string;
  body: string;
}
export interface SystemTemplatesView {
  value: Record<SystemTemplateKey, SystemTemplate>;
  version: number;
  updatedAt: string | null;
  defaults: Record<SystemTemplateKey, SystemTemplate>;
  keys: SystemTemplateKey[];
  variables: Record<SystemTemplateKey, string[]>;
}

export const systemTemplatesApi = {
  get: (signal?: AbortSignal) => api.get<SystemTemplatesView>('/platform/email/templates', undefined, { signal }),
  save: (value: Record<SystemTemplateKey, SystemTemplate>, expectedVersion: number) => api.put<SystemTemplatesView>('/platform/email/templates', { value, expectedVersion }),
  /** Renders with sample values; never sends. `html` is a preview-only document for a sandboxed iframe. */
  preview: (key: SystemTemplateKey, draft?: SystemTemplate) => api.post<SystemTemplatePreview>('/platform/email/templates/preview', { key, ...(draft ? { draft } : {}) }),
};

export interface SystemTemplatePreview {
  subject: string;
  text: string;
  /** Older servers omit it; the console then falls back to the plain text. */
  html?: string;
  sample: boolean;
}

export const EMAIL_LOG_BULK_DELETE_MAX = 100;
export interface EmailLogBulkDeleteResult {
  requested: number;
  deleted: number;
  skipped: number;
  skippedReasons: { not_found: number; in_flight: number; too_recent: number; inactivity_cooldown: number };
}

export interface EmailConfigView {
  value: EmailProviderSettings;
  version: number;
  updatedAt: string | null;
  password: { configured: boolean; masked: string | null; updatedAt: string | null };
  readiness: EmailReadiness;
  providers: EmailProviderKind[];
  environment: { driver: string; smtpUrlConfigured: boolean; from: string };
}

export interface EmailStatusView {
  health: 'ok' | 'degraded' | 'not_configured' | 'unknown';
  readiness: EmailReadiness;
  lastSuccess: { at: string; category: string; provider: string | null } | null;
  lastFailure: { at: string; category: string; provider: string | null; error: string | null; errorClass: string | null } | null;
  last24h: { sent: number; failed: number; queued: number; bouncedOrSuppressed: number };
}

export interface TestEmailResult {
  logId: string;
  status: 'sent' | 'failed';
  providerMessageId: string | null;
  error: string | null;
  errorClass: string | null;
  provider: string | null;
  sentAt: string | null;
}

export const emailApi = {
  config: (signal?: AbortSignal) => api.get<EmailConfigView>('/platform/email/config', undefined, { signal }),
  saveConfig: (value: EmailProviderSettings, expectedVersion: number) => api.put<EmailConfigView>('/platform/email/config', { value, expectedVersion }),
  setPassword: (password: string) => api.put<EmailConfigView['password']>('/platform/email/config/password', { password }),
  clearPassword: () => apiDelete<EmailConfigView['password']>('/platform/email/config/password'),
  test: (to: string) => api.post<TestEmailResult>('/platform/email/test', { to }),
  status: (signal?: AbortSignal) => api.get<EmailStatusView>('/platform/email/status', undefined, { signal }),
};

/* ---------------------------------------------------------------- email logs */

export interface EmailLogRow {
  id: string;
  createdAt: string;
  sentAt: string | null;
  to: string;
  subject: string;
  category: EmailCategory;
  status: EmailStatus;
  provider: string | null;
  providerMessageId: string | null;
  error: string | null;
  errorClass: string | null;
  attempts: number;
  tenant: { id: string; name: string | null } | null;
  userId: string | null;
}

export interface EmailLogFilters {
  category?: string;
  status?: string;
  tenantId?: string;
  recipient?: string;
  recipientMatch?: 'exact' | 'prefix';
  from?: string;
  to?: string;
  provider?: string;
}

export interface EmailLogSummary {
  range: { from: string; to: string };
  totals: { all: number; sent: number; failed: number; bouncedOrSuppressed: number; queued: number };
  byStatus: Record<string, number>;
  byCategory: Array<{ category: string; count: number }>;
  series: Array<{ key: string; label: string; points: Array<{ x: string; y: number }> }>;
}

export const emailLogApi = {
  summary: (range: { from: string; to: string }, signal?: AbortSignal) => api.get<EmailLogSummary>('/platform/email-logs/summary', range, { signal }),
  exportUrl: (filters: EmailLogFilters) => `${API_BASE}/platform/email-logs/export.csv${buildQuery(filters as Query)}`,
  bulkDelete: (ids: string[]) => api.post<EmailLogBulkDeleteResult>('/platform/email-logs/bulk-delete', { ids }),
};

/* ---------------------------------------------------------------- inactivity */

export interface InactivitySettings {
  enabled: boolean;
  inactiveDays: number;
  audience: 'tenant_admins' | 'all_staff';
  cooldownDays: number;
  subject: string;
  bodyText: string;
  maxPerRun: number;
}

export interface InactivitySettingsView {
  value: InactivitySettings;
  version: number;
  updatedAt: string | null;
  placeholders: string[];
}

export interface InactivityPreview {
  count: number;
  willSendThisRun: number;
  enabled: boolean;
  settings: Pick<InactivitySettings, 'inactiveDays' | 'cooldownDays' | 'audience'>;
  recipients: Array<{ userId: string; name: string; email: string; workspace: string; lastActiveAt: string }>;
  sample: { subject: string; text: string } | null;
}

export interface InactivityRun {
  id: string;
  status: string;
  manual: boolean;
  reason: string | null;
  createdAt: string;
  runAt: string;
  completedAt: string | null;
  suppressedReason: string | null;
  deferredReason: string | null;
  lastError: string | null;
  eligible: number;
  sent: number;
  failed: number;
  duplicates: number;
}

export interface CommsEmailRow {
  id: string;
  createdAt: string;
  sentAt: string | null;
  to: string;
  subject: string;
  status: EmailStatus;
  provider: string | null;
  error: string | null;
  errorClass: string | null;
  tenantId: string | null;
  userId: string | null;
  meta: Record<string, unknown>;
}

export const inactivityApi = {
  settings: (signal?: AbortSignal) => api.get<InactivitySettingsView>('/platform/inactivity/settings', undefined, { signal }),
  save: (value: InactivitySettings, expectedVersion: number) => api.put<InactivitySettingsView>('/platform/inactivity/settings', { value, expectedVersion }),
  preview: (q: Partial<Pick<InactivitySettings, 'inactiveDays' | 'cooldownDays' | 'audience'>>, signal?: AbortSignal) => api.get<InactivityPreview>('/platform/inactivity/preview', q, { signal }),
  runNow: (reason: string) => api.post<{ jobId: string; runAt: string }>('/platform/inactivity/run-now', { reason }),
  runs: (signal?: AbortSignal) => api.get<InactivityRun[]>('/platform/inactivity/runs', undefined, { signal }),
  summary: (signal?: AbortSignal) => api.get<InactivitySummary>('/platform/inactivity/summary', undefined, { signal }),
  test: (to?: string) => api.post<{ status: 'sent' | 'failed'; error: string | null; logId: string; to: string }>('/platform/inactivity/test', to ? { to } : {}),
};

export interface InactivitySummary {
  enabled: boolean;
  lastRun: { id: string; status: string; manual: boolean; finishedAt: string; sent: number; failed: number; note: string | null } | null;
  runsInProgress: number;
  nextRun: { at: string; basis: 'next_utc_day' | 'next_worker_check' } | null;
  worker: { alive: boolean; lastBeatAt: string | null };
  delivery30d: { sent: number; failed: number; bounced: number; queued: number };
}

/* ---------------------------------------------------------------- usage reports */

export const USAGE_REPORT_SECTIONS = ['tenants', 'users', 'engagements', 'emails', 'billing', 'ai'] as const;
export type UsageReportSection = (typeof USAGE_REPORT_SECTIONS)[number];

export interface UsageReportInput {
  name: string;
  recipients: string[];
  frequency: 'daily' | 'weekly' | 'monthly';
  dayOfWeek: number | null;
  dayOfMonth: number | null;
  hourUtc: number;
  sections: UsageReportSection[];
  enabled: boolean;
}

export interface UsageReportSchedule extends UsageReportInput {
  id: string;
  lastRunAt: string | null;
  nextRunAt: string | null;
  lastStatus: 'sent' | 'partial' | 'failed' | null;
  lastError: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface UsageReportPreview {
  report: { period: { start: string; end: string }; generatedAt: string; sections: Record<string, unknown> };
  subject: string;
  text: string;
}

export const usageReportApi = {
  create: (body: UsageReportInput) => api.post<UsageReportSchedule>('/platform/usage-reports', body),
  update: (id: string, body: UsageReportInput) => api.put<UsageReportSchedule>(`/platform/usage-reports/${id}`, body),
  remove: (id: string) => apiDelete<{ deleted: boolean }>(`/platform/usage-reports/${id}`),
  sendNow: (id: string, reason: string) => api.post<{ jobId: string }>(`/platform/usage-reports/${id}/send-now`, { reason }),
  preview: (id: string, signal?: AbortSignal) => api.get<UsageReportPreview>(`/platform/usage-reports/${id}/preview`, undefined, { signal }),
  draftPreview: (body: { name: string; frequency: UsageReportInput['frequency']; sections: UsageReportSection[] }) => api.post<UsageReportPreview>('/platform/usage-reports/preview', body),
};

/* ---------------------------------------------------------------- audience (shared targeting) */

/**
 * Mirrors apps/api/src/services/comms/audience.ts. Current values: tenant_admins, tenant_owners,
 * staff, roles, operators. Legacy values (all, clients, tenants, tenant_staff) are still accepted.
 */
export type TargetAudience = 'all' | 'staff' | 'clients' | 'tenant_admins' | 'tenant_owners' | 'roles' | 'operators' | 'tenants' | 'tenant_staff';

export interface AudienceValue {
  audience: TargetAudience;
  /** Tenant roles for the `roles` audience. */
  roles: string[];
  /** Empty = every organisation. */
  tenantIds: string[];
  /** Client-portal users are only reached when this is on (off by default). */
  includeClients: boolean;
}

export interface AudiencePreview {
  users: number;
  tenants: number;
  staff: number;
  clients: number;
  includesClients: boolean;
  sample: Array<{ name: string; email: string; role: string; tenant: string | null }>;
}

export const audienceApi = {
  preview: (v: AudienceValue, signal?: AbortSignal) => api.post<AudiencePreview>('/platform/audience/preview', { audience: v.audience, roles: v.roles, tenantIds: v.tenantIds, includeClients: v.includeClients }, { signal }),
};

/* ---------------------------------------------------------------- announcements */

export type AnnouncementSeverity = 'info' | 'warning' | 'critical';
export type AnnouncementAudience = TargetAudience;
export type AnnouncementStatus = 'draft' | 'scheduled' | 'active' | 'ended' | 'archived';
export type AnnouncementCategory = 'general' | 'feature' | 'maintenance' | 'billing' | 'security' | 'policy';

export interface AnnouncementInput extends AudienceValue {
  title: string;
  summary: string | null;
  body: string;
  severity: AnnouncementSeverity;
  category?: AnnouncementCategory;
  audience: AnnouncementAudience;
  startsAt: string;
  endsAt: string | null;
  dismissible: boolean;
  link: { label: string; url: string } | null;
  sendEmail: boolean;
  expectedVersion?: number;
}

export interface AnnouncementAdmin extends Omit<AnnouncementInput, 'expectedVersion'> {
  id: string;
  status: AnnouncementStatus;
  archivedAt?: string | null;
  publishedAt: string | null;
  endedAt: string | null;
  emailJobCreatedAt: string | null;
  emailCompletedAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  version: number;
}

export interface AnnouncementDetail extends AnnouncementAdmin {
  delivery: { emails: { total: number; sent: number; failed: number; queued: number; bouncedOrSuppressed: number; lastSentAt: string | null }; dismissals: number };
  emailAudienceSize: number | null;
}

export interface ActiveAnnouncement {
  id: string;
  title: string;
  summary?: string | null;
  body: string;
  severity: AnnouncementSeverity;
  dismissible: boolean;
  link: { label: string; url: string } | null;
  startsAt: string;
  endsAt: string | null;
}

export const announcementApi = {
  get: (id: string, signal?: AbortSignal) => api.get<AnnouncementDetail>(`/platform/announcements/${id}`, undefined, { signal }),
  create: (body: AnnouncementInput) => api.post<AnnouncementAdmin>('/platform/announcements', body),
  update: (id: string, body: AnnouncementInput) => api.put<AnnouncementAdmin>(`/platform/announcements/${id}`, body),
  publish: (id: string) => api.post<AnnouncementAdmin>(`/platform/announcements/${id}/publish`),
  schedule: (id: string, startsAt: string) => api.post<AnnouncementAdmin>(`/platform/announcements/${id}/schedule`, { startsAt }),
  end: (id: string) => api.post<AnnouncementAdmin>(`/platform/announcements/${id}/end`),
  archive: (id: string) => api.post<AnnouncementAdmin>(`/platform/announcements/${id}/archive`),
  unarchive: (id: string) => api.post<AnnouncementAdmin>(`/platform/announcements/${id}/unarchive`),
  remove: (id: string) => apiDelete<{ deleted: boolean }>(`/platform/announcements/${id}`),
  active: (signal?: AbortSignal) => api.get<ActiveAnnouncement[]>('/announcements/active', undefined, { signal }),
  dismiss: (id: string) => api.post<{ dismissed: boolean }>(`/announcements/${id}/dismiss`),
};

/* ---------------------------------------------------------------- release notes */

export type ReleaseNoteCategory = 'feature' | 'improvement' | 'fix' | 'security';
export type ReleaseNoteAudience = TargetAudience;
export type ReleaseNotePriority = 'low' | 'normal' | 'high';
export type ReleaseNoteState = 'draft' | 'scheduled' | 'published' | 'expired' | 'archived';

export interface ReleaseNoteInput extends AudienceValue {
  title: string;
  summary: string | null;
  body: string;
  version: string | null;
  category: ReleaseNoteCategory;
  priority: ReleaseNotePriority;
  audience: ReleaseNoteAudience;
  publishAt: string | null;
  expiresAt: string | null;
  videoUrl: string | null;
  linkUrl: string | null;
  linkLabel: string | null;
}

export interface ReleaseNote extends ReleaseNoteInput {
  id: string;
  status: 'draft' | 'published';
  /** Derived lifecycle state (older API responses omit it). */
  state?: ReleaseNoteState;
  publishedAt: string | null;
  archivedAt?: string | null;
  scheduled?: boolean;
  createdAt: string | null;
  updatedAt: string | null;
}

export const releaseNoteApi = {
  create: (body: ReleaseNoteInput) => api.post<ReleaseNote>('/platform/release-notes', body),
  update: (id: string, body: ReleaseNoteInput) => api.put<ReleaseNote>(`/platform/release-notes/${id}`, body),
  publish: (id: string) => api.post<ReleaseNote>(`/platform/release-notes/${id}/publish`),
  schedule: (id: string, publishAt: string) => api.post<ReleaseNote>(`/platform/release-notes/${id}/schedule`, { publishAt }),
  unpublish: (id: string) => api.post<ReleaseNote>(`/platform/release-notes/${id}/unpublish`),
  archive: (id: string) => api.post<ReleaseNote>(`/platform/release-notes/${id}/archive`),
  unarchive: (id: string) => api.post<ReleaseNote>(`/platform/release-notes/${id}/unarchive`),
  remove: (id: string, confirmTitle?: string) => api.raw<{ data: { deleted: boolean } }>(`/platform/release-notes/${id}`, { method: 'DELETE', ...(confirmTitle ? { body: { confirmTitle } } : {}) }).then((b) => b?.data),
  feed: (q: { cursor?: string; limit?: number }, signal?: AbortSignal) => api.list<ReleaseNote>('/updates', q, { signal }),
  unread: (signal?: AbortSignal) => api.get<{ unread: number; lastSeenAt: string | null }>('/updates/unread-count', undefined, { signal }),
  markSeen: () => api.post<{ lastSeenAt: string; unread: number }>('/updates/seen'),
};

export type { Page };

/* ---------------------------------------------------------------- labels */

export const CATEGORY_LABEL: Record<string, string> = {
  invitation: 'Invitation',
  password_reset: 'Password reset',
  reminder: 'Reminder',
  login_alert: 'Login alert',
  usage_report: 'Usage report',
  inactivity: 'Inactivity',
  announcement: 'Announcement',
  security: 'Security',
  test: 'Test',
  other: 'Other',
};

export const STATUS_TONE: Record<string, 'success' | 'danger' | 'warning' | 'neutral' | 'info' | 'muted'> = {
  sent: 'success',
  delivered: 'success',
  failed: 'danger',
  bounced: 'warning',
  suppressed: 'warning',
  queued: 'neutral',
};

/** Renders a UTC hour as "09:00 UTC". */
export const hourLabel = (h: number) => `${String(h).padStart(2, '0')}:00 UTC`;
export const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
